import React, {ReactNode} from "react";
import SortHelper from "../../shared/helper/SortHelper";
import DocumentFilter, {DocumentFilterInterface} from "../document/DocumentFilter";
import {PortalDocumentManagerGlobals} from "../document/PortalDocumentManager";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import PortalContext from "../PortalContext";
import MenuButton from "../../shared/menu/MenuButton";
import Immutable from "../../shared/immutable/Immutable";
import {MenuItem} from "primereact/menuitem";
import Translate from "../../shared/translation/Translate";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {DocumentFile} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

export interface DocumentFilterGlobals {
	documentFilter: Immutable<DocumentFilterInterface>;
}

export function initDocumentFilterGlobals(): DocumentFilterGlobals {
	return {
		documentFilter: Immutable.from(new DocumentFilter())
	};
}

interface DocumentFilterProperties extends DocumentFilterGlobals, PortalDocumentManagerGlobals, I18nGlobals {
	context: PortalContext,
}

export default class DocumentFilterComponent extends PortalPureComponent<DocumentFilterProperties, any> {
	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let menuItems: Array<MenuItem> = [];
		let documentFilter: DocumentFilter = DocumentFilter.fromJson(this.props.documentFilter.get());

		let fileTypes: Array<string> = [];
		for (let portalDocument of this.props.context.documentManager.getDocuments()) {
			let type: string = FunctionHelper.applyValue(
				portalDocument.getDocumentFile()?.fileExtension?.toUpperCase(),
				DocumentFile.getFileExtensionDefault()
			);

			if (fileTypes.indexOf(type) === -1) {
				fileTypes.push(type);
			}
		}

		fileTypes.sort(SortHelper.alphanum);

		for (let fileType of fileTypes) {
			menuItems.push({
				template: (
					<a href={fileType} className="p-menuitem-link" role="menuitem" onClick={
						(event) => {
							event.preventDefault();

							let typeIndex: number = documentFilter.filteredExtensions.indexOf(fileType);

							if (typeIndex === -1) {
								documentFilter.filteredExtensions.push(fileType);
							} else {
								documentFilter.filteredExtensions.splice(typeIndex, 1);
							}

							this.props.context.getPage().setState({
								documentFilter: Immutable.from(documentFilter.toJson())
							});
						}
					}>
						<span className={
							"p-menuitem-icon icon" + (
								documentFilter.filteredExtensions.indexOf(fileType.toUpperCase()) === -1 ? " icon-check" : " empty"
							)
						}/>
						<span className="p-menuitem-text" style={{
							flexGrow: 1
						}}>
							{(fileType !== "") ? fileType :
								Translate.value("document_bar.filter.no_ext", {ns: "menu"})
							}
						</span>
					</a>
				)
			});
		}

		menuItems = menuItems.concat([
			{separator: true},
			{
				template: (
					<a href={"pdfa"} className="p-menuitem-link" role="menuitem" onClick={
						(event) => {
							event.preventDefault();

							documentFilter.pdfaRequired = !documentFilter.pdfaRequired;

							this.props.context.getPage().setState({
								documentFilter: Immutable.from(documentFilter.toJson())
							});
						}
					}>
						<span className={
							"p-menuitem-icon icon" + (documentFilter.pdfaRequired ? " icon-check" : " empty")
						}/>
						<span className="p-menuitem-text" style={{
							flexGrow: 1
						}}>
							 <Translate {...this.props} data="document_bar.filter.pdfa" namespace="menu"/>
						</span>
					</a>
				)
			},
			{
				template: (
					<a href={"password"} className="p-menuitem-link" role="menuitem" onClick={
						(event) => {
							event.preventDefault();

							documentFilter.passwordRequired = !documentFilter.passwordRequired;

							this.props.context.getPage().setState({
								documentFilter: Immutable.from(documentFilter.toJson())
							});
						}
					}>
						<span className={
							"p-menuitem-icon icon" + (documentFilter.passwordRequired ? " icon-check" : " empty")
						}/>
						<span className="p-menuitem-text" style={{
							flexGrow: 1
						}}>
							 <Translate {...this.props} data="document_bar.filter.password" namespace="menu"/>
						</span>
					</a>
				)
			},
			{
				template: (
					<a href={"signature"} className="p-menuitem-link" role="menuitem" onClick={
						(event) => {
							event.preventDefault();

							documentFilter.signatureRequired = !documentFilter.signatureRequired;

							this.props.context.getPage().setState({
								documentFilter: Immutable.from(documentFilter.toJson())
							});
						}
					}>
						<span className={
							"p-menuitem-icon icon" + (documentFilter.signatureRequired ? " icon-check" : " empty")
						}/>
						<span className="p-menuitem-text" style={{
							flexGrow: 1
						}}>
							 <Translate {...this.props} data="document_bar.filter.signature" namespace="menu"/>
						</span>
					</a>
				)
			},
			{
				template: (
					<a href={"attachments"} className="p-menuitem-link" role="menuitem" onClick={
						(event) => {
							event.preventDefault();

							documentFilter.attachmentsRequired = !documentFilter.attachmentsRequired;

							this.props.context.getPage().setState({
								documentFilter: Immutable.from(documentFilter.toJson())
							});
						}
					}>
						<span className={
							"p-menuitem-icon icon" + (documentFilter.attachmentsRequired ? " icon-check" : " empty")
						}/>
						<span className="p-menuitem-text" style={{
							flexGrow: 1
						}}>
							 <Translate {...this.props} data="document_bar.filter.attachments" namespace="menu"/>
						</span>
					</a>
				)
			},
			{separator: true},
			{
				template: (
					<a href={"all"} className="p-menuitem-link" role="menuitem" onClick={
						(event) => {
							event.preventDefault();

							documentFilter.reset();

							this.props.context.getPage().setState({
								documentFilter: Immutable.from(documentFilter.toJson())
							});
						}
					}>
						<span className={
							"p-menuitem-icon icon" + (!documentFilter.isFiltered() ? " icon-check" : " empty")
						}/>
						<span className="p-menuitem-text" style={{
							flexGrow: 1
						}}>
							 <Translate {...this.props} data="document_bar.filter.all" namespace="menu"/>
						</span>
					</a>
				)
			}
		]);

		return (
			<MenuButton {...this.props} className={documentFilter.isFiltered() ? "active" : ""}
						disabled={this.props.portalDocuments.get().length === 0} icon="icon icon-filter"
						tooltip={Translate.value("document_bar.filter.tooltip", {ns: "menu"})}
						menuItems={menuItems} tooltipOptions={{position: 'left'}}/>
		);
	}
}